import { metersToKilometers, metersToNauticalMiles, useSettings } from '@marine/services';
import dayjs, { extend } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback } from 'react';

extend(utc);

/**
 * Distances are shown in the unit the user asked for, the same way the logs table and the log form
 * do it: nautical miles for an imperial user, kilometres for a metric one. One decimal, because
 * rounding a trip distance to whole units prints "0" for every short hop.
 */
const DISTANCE_FRACTION_DIGITS = 1;

const useFormattedValues = () => {
  const { getSettingByKey } = useSettings();

  const getFormattedDate = useCallback(
    (date: any, isUtc = true) => {
      if (date && dayjs(date).isValid()) {
        if (isUtc) {
          return dayjs.utc(date).local().format(getSettingByKey('date_format'));
        }

        return dayjs(date).format(getSettingByKey('date_format'));
      }

      return '';
    },
    [getSettingByKey],
  );

  const getFormattedDateTime = useCallback(
    (date: any, isUtc = true) => {
      if (date && dayjs(date).isValid()) {
        if (isUtc) {
          return dayjs
            .utc(date)
            .local()
            .format(`${getSettingByKey('date_format')} ${getSettingByKey('time_format')}`);
        }

        return dayjs(date).format(`${getSettingByKey('date_format')} ${getSettingByKey('time_format')}`);
      }

      return '';
    },
    [getSettingByKey],
  );

  const getFormattedTime = useCallback(
    (date: any, isUtc = true) => {
      if (date && dayjs(date).isValid()) {
        if (isUtc) {
          return dayjs.utc(date).local().format(getSettingByKey('time_format'));
        }

        return dayjs(date).format(getSettingByKey('time_format'));
      }

      return '';
    },
    [getSettingByKey],
  );

  const getFormattedDistance = useCallback(
    (meters: number | null | undefined) => {
      if (typeof meters !== 'number' || !Number.isFinite(meters)) {
        return '';
      }

      const isImperial = getSettingByKey('unit') === 'imperial';
      const value = isImperial ? metersToNauticalMiles(meters) : metersToKilometers(meters);

      return `${value.toFixed(DISTANCE_FRACTION_DIGITS)} ${isImperial ? 'NM' : 'KM'}`;
    },
    [getSettingByKey],
  );

  return { getFormattedDate, getFormattedDateTime, getFormattedTime, getFormattedDistance };
};

export { useFormattedValues };
